import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './IndexPage.module.css';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import { createSystemLoginApiClient } from '../utils/systemLoginApi';

type Role = 'user' | 'assistant';
type PageMode = 'generate' | 'upload' | 'submit';

interface ChatMessageItem {
  id: string;
  role: Role;
  content: string;
  imageUrl?: string;
  isGenerating?: boolean;
  attachments?: UploadedFile[];
}

interface UploadedFile {
  id: string;
  file: File;
  url: string;
  fileId?: string;
  isUploading?: boolean;
}

interface ReferencedImage {
  id: string;
  url: string;
  messageId: string;
  messageContent: string;
}

interface SubmitToyDesignedImageRequest {
  userId?: string;
  shopId?: string;
  submitId?: number;
  submitDescription: string;
  imageUrl: string;
}

// 查询请求接口
interface QueryGeneratedToyImageHistoryRequest {
  userId?: string;
  shopId?: string;
  keyword?: string;
  monthStr?: string;
  taskType: number;
  page: number;
  pageSize: number;
}

interface QueryGeneratedToyImageListRequest {
  userId?: string;
  shopId?: string;
  keyword?: string;
  monthStr?: string;
  page: number;
  pageSize: number;
}

interface QuerySubmitedToyImageHistoryRequest {
  userId?: string;
  shopId?: string;
  keyword?: string;
  monthStr?: string;
  page: number;
  pageSize: number;
}

// API 响应接口
interface GeneratedToyImageHistoryResponse {
  history: GeneratedToyImageItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

interface GeneratedToyImageItem {
  task_type: number;
  task_status: number;
  generate_prompt: string;
  uploaded_image_url: string;
  generate_image_api_name: string;
  generate_image_count: number;
  generate_image_url: string;
  generate_image_description: string;
}

interface GeneratedToyImageListResponse {
  imageList: any[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

interface SubmitToyDesignedImageResponse {
  history: SubmitToyDesignedImageItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

interface SubmitToyDesignedImageItem {
  id: number;
  submit_sescription: string;
  image_url: string;
}


const IndexPage: React.FC = () => {
  const systemLogin = useSystemLogin();
  const navigate = useNavigate();
  const apiClient = useMemo(() => createSystemLoginApiClient(systemLogin), [systemLogin]);

  // 分别为不同模式维护独立的会话
  const [generateMessages, setGenerateMessages] = useState<ChatMessageItem[]>([{
    id: 'welcome-generate',
    role: 'assistant',
    content: '你好，我是你的智能助理。请描述你想要生成的玩具图片，我会为你创作出来。',
  }]);
  
  const [uploadMessages, setUploadMessages] = useState<ChatMessageItem[]>([{
    id: 'welcome-upload',
    role: 'assistant',
    content: '你好，我是你的智能助理。请上传你的玩具图片，我会为你分析和处理。',
  }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [referencedImage, setReferencedImage] = useState<ReferencedImage | null>(null);
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 页面模式与侧栏收起
  const [mode, setMode] = useState<PageMode>('generate');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // 用户下拉菜单状态
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  
  // 图片生成超时检测
  const [generatingTimeoutId, setGeneratingTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // 提交设计状态
  const [designDesc, setDesignDesc] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [availableImages, setAvailableImages] = useState<string[]>([]);
  const [designSubmitting, setDesignSubmitting] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  // 提交记录
  const [designHistory, setDesignHistory] = useState<SubmitToyDesignedImageRequest[]>([]);

  // WebSocket 连接状态
  const [wsConnected, setWsConnected] = useState(false);
  const [wsReconnectAttempts, setWsReconnectAttempts] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 心跳机制相关状态
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [heartbeatMissed, setHeartbeatMissed] = useState<number>(0);
  
  // 使用SystemLogin的登录状态
  const isLoggedIn = systemLogin.isLoggedIn;
  
  // 用户信息检查状态
  const [userProfileChecked, setUserProfileChecked] = useState(false);
  const [isCheckingUserProfile, setIsCheckingUserProfile] = useState(false);
  
  // 心跳机制常量
  const HEARTBEAT_INTERVAL = 80000; // 80秒发送一次心跳
  const HEARTBEAT_TIMEOUT = 10000;  // 10秒内没有收到心跳响应则认为连接异常
  const MAX_HEARTBEAT_MISSED = 3;   // 最大允许连续丢失的心跳次数
  const HEARTBEAT_REQUEST_MESSAGE = 'Pioneer_Heartbeat_Request';   // 心跳请求消息
  const HEARTBEAT_RESPONSE_MESSAGE = 'Pioneer_Heartbeat_Response'; // 心跳响应消息

  // 启动心跳机制
  const startHeartbeat = () => {
    console.log('启动 WebSocket 心跳机制');
    
    // 清理旧的心跳定时器
    stopHeartbeat();
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(HEARTBEAT_REQUEST_MESSAGE);
          console.log('发送心跳:', HEARTBEAT_REQUEST_MESSAGE);
          
          // 设置心跳超时检查
          heartbeatTimeoutRef.current = setTimeout(() => {
            console.warn('心跳响应超时');
            setHeartbeatMissed(prev => {
              const newMissed = prev + 1;
              console.log(`心跳丢失次数: ${newMissed}/${MAX_HEARTBEAT_MISSED}`);
              
              if (newMissed >= MAX_HEARTBEAT_MISSED) {
                console.error('连续心跳丢失超过阈值，主动断开连接');
                if (wsRef.current) {
                  wsRef.current.close(1001, '心跳超时');
                }
              }
              return newMissed;
            });
          }, HEARTBEAT_TIMEOUT);
          
        } catch (error) {
          console.error('发送心跳失败:', error);
        }
      } else {
        console.warn('WebSocket 未连接，停止心跳');
        stopHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);
  };

  // 提交记录
  const loadDesignHistory = async () => {
    if (!systemLogin.isLoggedIn) return;
    try {
      const requestData: QuerySubmitedToyImageHistoryRequest = {
        page: 1,
        pageSize: 10
      };

      const { response, isLoginNeeded } = await systemLogin.protectPostRequest<SubmitToyDesignedImageResponse>(
        '/app-api/ai/agent/querySubmittedToyImageHistory',
        undefined,
        undefined,
        requestData
      );

      if (isLoginNeeded) {
        // 可能需要导航到登录页面
        return;
      }

      if (response?.code === 1 && response.data) {
        console.log(`获取提交设计历史数据成功:`, response.data);
        
        const data = response.data;
        // 确保 data 是数组或包含数组的属性
        let historyList: SubmitToyDesignedImageItem[] = [];
        if (Array.isArray(data.history)) {
          historyList = data.history;
        }
        
        setDesignHistory(historyList.map((item: any) => ({
          userId: item.userId,
          shopId: item.shopId,
          submitId: item.submit_id || item.id,
          submitDescription: item.submit_description || item.description || '',
          imageUrl: item.image_url || item.image_urls?.[0] || ''
        })));
      }
    } catch (error) {
      console.error('查询提交设计历史失败:', error);
      setDesignHistory([]);
    }
  };

  // 查询生成图片或上传图片记录的对话历史
  const loadGeneratedToyImageHistory = async (taskType: number, page: number, pageSize: number) => {
    if (!systemLogin.isLoggedIn) return;
    try {
      const requestData: QueryGeneratedToyImageHistoryRequest = {
        taskType: taskType, // 1: 生成图片, 2: 上传图片
        page: page,
        pageSize: pageSize
      };

      const { response, isLoginNeeded } = await systemLogin.protectPostRequest<GeneratedToyImageHistoryResponse>(
        '/app-api/ai/agent/queryGeneratedToyImageChatHistory',
        undefined,
        undefined,
        requestData
      );

      if (isLoginNeeded) {
        // 可能需要导航到登录页面
        return;
      }

      if (response?.code === 1 && response.data) {
        console.log(`获取生成图片历史数据成功:`, response.data);
        
        const data = response.data;
        // 确保 data 是数组或包含数组的属性
        let historyList: GeneratedToyImageItem[] = [];
        if (Array.isArray(data.history)) {
          historyList = data.history;
        }
        
        // 将历史记录转换为聊天消息格式
        const historyMessages: ChatMessageItem[] = [];
        historyList.forEach((item, index) => {
          // 用户消息
          if (item.generate_prompt || item.uploaded_image_url) {
            historyMessages.push({
              id: `history-user-${index}`,
              role: 'user',
              content: item.generate_prompt || (item.uploaded_image_url ? '（已上传图片）' : ''),
              imageUrl: item.uploaded_image_url || undefined
            });
          }
          
          // AI回复消息
          if (item.generate_image_description || item.generate_image_url) {
            historyMessages.push({
              id: `history-ai-${index}`,
              role: 'assistant',
              content: item.generate_image_description || (item.generate_image_url ? '图片处理完成' : ''),
              imageUrl: item.generate_image_url || undefined
            });
          }
        });
        
        // 根据任务类型更新对应的消息列表
        if (taskType === 1) {
          // 生成图片历史
          setGenerateMessages(prev => {
            // 保留欢迎消息和当前对话，只添加新的历史记录
            const welcomeMessage = prev.find(msg => msg.id === 'welcome-generate');
            const currentMessages = prev.filter(msg => !msg.id.startsWith('history-') && msg.id !== 'welcome-generate');
            return welcomeMessage ? [welcomeMessage, ...historyMessages, ...currentMessages] : [...historyMessages, ...currentMessages];
          });
        } else if (taskType === 2) {
          // 上传图片历史
          setUploadMessages(prev => {
            // 保留欢迎消息和当前对话，只添加新的历史记录
            const welcomeMessage = prev.find(msg => msg.id === 'welcome-upload');
            const currentMessages = prev.filter(msg => !msg.id.startsWith('history-') && msg.id !== 'welcome-upload');
            return welcomeMessage ? [welcomeMessage, ...historyMessages, ...currentMessages] : [...historyMessages, ...currentMessages];
          });
        }
      }
    } catch (error) {
      console.error('查询生成图片历史失败:', error);
    }
  };

  // 查询可用的图片列表
  const loadAvailableImages = async () => {
    if (!systemLogin.isLoggedIn) return;
    setLoadingImages(true);
    try {
      const requestData: QueryGeneratedToyImageListRequest = {
        page: 1,
        pageSize: 50
      };

      const { response, isLoginNeeded } = await systemLogin.protectPostRequest<GeneratedToyImageListResponse>(
        '/app-api/ai/agent/queryGeneratedToyImageList',
        undefined,
        undefined,
        requestData
      );

      if (isLoginNeeded) {
        // 可能需要导航到登录页面
        return;
      }

      if (response?.code === 1 && response.data) {
        console.log(`获取可用的图片列表数据成功:`, response.data);
        
        const data = response.data;
        // 确保 imageList 存在且是数组
        if (data.imageList && Array.isArray(data.imageList)) {
          const imageList = data.imageList;
          setAvailableImages(imageList);
        } else {
          setAvailableImages([]);
        }
      }
    } catch (error) {
      console.error('查询图片列表失败:', error);
      setAvailableImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // 选择图片
  const handleImageSelect = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
  };

  // 点击历史记录项，跳转到提交设计页面并预填充数据
  const handleHistoryItemClick = (item: SubmitToyDesignedImageRequest) => {
    setMode('submit');
    setDesignDesc(item.submitDescription || '');
    setSelectedImageUrl(item.imageUrl || '');
    setEditingItemId(item.submitId || null);
    // 重新加载图片列表以确保选中的图片在列表中
    void loadAvailableImages();
  };

  // 检查用户是否已添加用户信息
  const checkUserProfile = async () => {
    if (!isLoggedIn || userProfileChecked || isCheckingUserProfile) {
      return;
    }

    setIsCheckingUserProfile(true);
    try {
      // 查询用户地址列表，不传cityId参数获取所有地址
      const result = await apiClient.safeGetUserAddressList('1534422005300989953');
      
      if (result.isLoginNeeded) {
        // 需要重新登录
        return;
      }

      if (result.error) {
        console.error('检查用户信息失败:', result.error);
        setUserProfileChecked(true);
        return;
      }

      // 检查是否有用户地址信息
      const addressList = result.data || [];
      if (addressList.length === 0) {
        // 没有用户信息，跳转到profile页面
        console.log('用户未添加地址信息，跳转到profile页面');
        navigate('/profile', { state: { isFirstTime: true } });
        return;
      }

      setUserProfileChecked(true);
    } catch (error) {
      console.error('检查用户信息异常:', error);
      setUserProfileChecked(true);
    } finally {
      setIsCheckingUserProfile(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      void checkUserProfile();
      void loadDesignHistory();
      void loadAvailableImages();
      // 根据当前模式加载对应的历史记录
      if (mode === 'generate') {
        void loadGeneratedToyImageHistory(1, 1, 10);
      } else if (mode === 'upload') {
        void loadGeneratedToyImageHistory(2, 1, 10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  
  // 停止心跳机制
  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    
    setHeartbeatMissed(0);
    console.log('WebSocket 心跳机制已停止');
  };
  
  // 处理心跳响应
  const handleHeartbeatResponse = () => {
    console.log('收到心跳响应');
    
    // 清除心跳超时定时器
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    
    // 重置心跳丢失计数
    setHeartbeatMissed(0);
  };

  // 处理业务消息
  const handleBusinessMessage = (data: any) => {
    console.log('处理业务消息:', data);
    
    // 从 messageContent 中获取 dataType
    const dataType = data.messageContent?.dataType || data.messageType;
    
    // 获取消息内容，优先使用 messageContent，否则使用原始 data
    const messageContent = data.messageContent || data;
    
    console.log('消息数据类型:', dataType, '消息内容:', messageContent);
    
    switch (dataType) {
      case 'GOING_TO_GENERATE_IMAGE':
        console.log('开始生成图片');
        setGenerateMessages(prev => prev.map(msg => 
          msg.isGenerating ? { ...msg, content: '正在生成图片...' } : msg
        ));
        break;
        
      case 'IMAGE_GENERATION_COMPLETED':
        console.log('图片生成完成:', messageContent);
        const imageUrl = messageContent.generatedImageUrl;
        if (imageUrl) {
          // 清除超时检测
          if (generatingTimeoutId) {
            clearTimeout(generatingTimeoutId);
            setGeneratingTimeoutId(null);
          }
          
          setGenerateMessages(prev => prev.map(msg => 
            msg.isGenerating ? { 
              ...msg, 
              content: '图片生成完成！',
              imageUrl: imageUrl,
              isGenerating: false
            } : msg
          ));
        }
        break;
        
      case 'IMAGE_GENERATION_ERROR':
        console.log('图片生成失败:', messageContent);
        // 清除超时检测
        if (generatingTimeoutId) {
          clearTimeout(generatingTimeoutId);
          setGeneratingTimeoutId(null);
        }
        
        setGenerateMessages(prev => prev.map(msg => 
          msg.isGenerating ? { 
            ...msg, 
            content: '图片生成失败: ' + (messageContent.error || '未知错误'),
            isGenerating: false
          } : msg
        ));
        break;
        
      case 'IMAGE_UPLOAD_PROCESSING':
        console.log('图片上传处理中:', messageContent);
        setUploadMessages(prev => prev.map(msg => 
          msg.isGenerating ? { ...msg, content: '正在处理上传的图片...' } : msg
        ));
        break;
        
      case 'IMAGE_UPLOAD_COMPLETED':
        console.log('图片上传处理完成:', messageContent);
        const uploadResult = messageContent.generatedDescription;
        if (uploadResult) {
          setUploadMessages(prev => prev.map(msg => 
            msg.isGenerating ? { 
              ...msg, 
              content: uploadResult,
              isGenerating: false
            } : msg
          ));
        }
        break;
        
      case 'IMAGE_UPLOAD_ERROR':
        console.log('图片上传处理失败:', messageContent);
        setUploadMessages(prev => prev.map(msg => 
          msg.isGenerating ? { 
            ...msg, 
            content: '图片处理失败: ' + (messageContent.error || '未知错误'),
            isGenerating: false
          } : msg
        ));
        break;
        
      default:
        console.log('未处理的消息类型:', dataType, data);
    }
  };
  
  // WebSocket 连接管理函数
  const connectWebSocket = (chatSessionId: string) => {
    // 构建 WebSocket URL
    const wsUrl = `wss://comm.aicaster.com.cn:10143/pioneer/deviceId/${chatSessionId}/ws`;
    
    console.log('正在连接 WebSocket:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket 连接已建立');
        setWsConnected(true);
        setWsReconnectAttempts(0);
        setHeartbeatMissed(0);
        
        // 启动心跳机制
        startHeartbeat();
      };
      
      ws.onmessage = (event) => {
        const message = event.data;
        console.log('收到 WebSocket 消息:', message);
        
        // 处理心跳响应
        if (message === HEARTBEAT_RESPONSE_MESSAGE) {
          handleHeartbeatResponse();
          return;
        }
        
        // 处理其他类型的消息（可能是JSON格式的业务消息）
        try {
          const msgBody = JSON.parse(message);
          const data = msgBody.message;
          // 处理业务消息
          handleBusinessMessage(data);
        } catch (error) {
          // 如果不是JSON格式，可能是其他类型的字符串消息
          console.log('收到非JSON消息:', message);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket 连接错误:', error);
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket 连接已关闭:', event.code, event.reason);
        setWsConnected(false);
        wsRef.current = null;
        
        // 停止心跳机制
        stopHeartbeat();
        
        // 如果不是主动关闭，则尝试重连
        if (event.code !== 1000 && wsReconnectAttempts < 5) {
          const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts), 30000); // 指数退避，最大30秒
          console.log(`WebSocket 将在 ${delay}ms 后尝试重连 (第 ${wsReconnectAttempts + 1} 次)`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            setWsReconnectAttempts(prev => prev + 1);
            connectWebSocket(chatSessionId);
          }, delay);
        }
      };
      
    } catch (error) {
      console.error('创建 WebSocket 连接失败:', error);
    }
  };
  
  // 断开 WebSocket 连接
  const disconnectWebSocket = () => {
    // 停止心跳机制
    stopHeartbeat();
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, '用户主动断开');
      wsRef.current = null;
    }
    
    setWsConnected(false);
    setWsReconnectAttempts(0);
    setHeartbeatMissed(0);
  };

  // 确保已创建会话
  useEffect(() => {
    const ensureSession = async () => {
      if (systemLogin.isLoggedIn && !systemLogin.currentUserChatSession) {
        try {
          await systemLogin.getOrCreateLatestUserChatSession();
        } catch (e) {
          // 忽略错误，UI提示由后续交互处理
        }
      }
    };
    ensureSession();
  }, [systemLogin.isLoggedIn, systemLogin.currentUserChatSession, systemLogin]);

  // 在 UserChatSession 初始化完成后建立 WebSocket 连接
  useEffect(() => {
    if (isLoggedIn && systemLogin.currentUserChatSession && !wsConnected && !wsRef.current) {
      console.log('正在为聊天会话建立 WebSocket 连接:', systemLogin.currentUserChatSession.id);
      connectWebSocket(systemLogin.currentUserChatSession.id);
    }
    
    // 清理函数：组件卸载或用户登出时断开连接
    return () => {
      if (!isLoggedIn) {
        disconnectWebSocket();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, systemLogin.currentUserChatSession?.id]);
  
  // 点击外部区域关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // 组件卸载时清理 WebSocket 连接和超时检测
  useEffect(() => {
    return () => {
      disconnectWebSocket();
      // 清理生成超时检测
      if (generatingTimeoutId) {
        clearTimeout(generatingTimeoutId);
        setGeneratingTimeoutId(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 根据当前模式获取对应的消息列表
  const currentMessages = mode === 'generate' ? generateMessages : uploadMessages;
  
  // 自动滚动到底部
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [currentMessages, sending]);

  // 文件上传相关函数
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mode !== 'upload') return;
    const files = event.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      const fileUrl = URL.createObjectURL(file);
      newFiles.push({
        id: Date.now() + Math.random().toString(),
        file,
        url: fileUrl,
        isUploading: false
      });
    });

    setAttachments(prev => [...prev, ...newFiles]);
    
    // 清空 input 值，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const fileToRemove = prev.find(file => file.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return prev.filter(file => file.id !== id);
    });
  };

  // 上传单个文件到服务器
  const uploadSingleFile = async (file: File): Promise<string> => {
    try {
      const response = await apiClient.uploadFiles('/app-api/upload/file/store', {
        files: { file: file }
      });
      
      if (response && typeof response === 'number') {
        return response.toString();
      } else if (response && typeof response === 'string') {
        return response;
      } else if (response && typeof response === 'object' && 'fileId' in response) {
        const fileId = (response as any).fileId;
        return typeof fileId === 'number' ? fileId.toString() : String(fileId);
      } else {
        throw new Error('服务器返回的文件ID格式不正确');
      }
    } catch (error) {
      console.error('上传文件失败:', error);
      throw new Error(`上传文件失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const triggerFileInput = () => {
    if (!systemLogin.isLoggedIn) {
      alert('请先登录');
      return;
    }
    if (mode !== 'upload') {
      return;
    }
    fileInputRef.current?.click();
  };

  // 左侧菜单：生成玩具图片
  const handleMenuGenerateClick = () => {
    setMode('generate');
    setInput('');
    setAttachments([]);
    setReferencedImage(null);
    // 加载生成图片历史记录
    void loadGeneratedToyImageHistory(1, 1, 10);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // 左侧菜单：上传玩具图片
  const handleMenuUploadClick = () => {
    setMode('upload');
    setReferencedImage(null);
    setInput('');
    // 加载上传图片历史记录
    void loadGeneratedToyImageHistory(2, 1, 10);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // 左侧菜单：提交设计
  const handleMenuSubmitDesignClick = () => {
    setMode('submit');
    // 重置编辑状态
    setEditingItemId(null);
    setDesignDesc('');
    setSelectedImageUrl(null);
    // 切换模式时重新加载图片列表
    void loadAvailableImages();
  };

  // 引用图片功能
  const handleImageReference = (imageUrl: string, messageId: string, messageContent: string) => {
    setReferencedImage({
      id: `ref-${Date.now()}`,
      url: imageUrl,
      messageId,
      messageContent: messageContent.substring(0, 50) + (messageContent.length > 50 ? '...' : '')
    });
  };

  // 取消引用图片
  const removeReferencedImage = () => {
    setReferencedImage(null);
  };

  // 下载图片功能
  const downloadImage = async (imageUrl: string, messageId: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-image-${messageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载图片失败:', error);
      alert('下载图片失败，请重试');
    }
  };

  // 将引用的图片转换为文件上传
  const convertReferencedImageToFile = async () => {
    if (!referencedImage) return;

    try {
      const response = await fetch(referencedImage.url);
      const blob = await response.blob();
      const file = new File([blob], `referenced-image-${referencedImage.id}.png`, { type: 'image/png' });
      
      const fileUrl = URL.createObjectURL(file);
      const newAttachment: UploadedFile = {
        id: `ref-file-${Date.now()}`,
        file,
        url: fileUrl,
        isUploading: false
      };
      if (mode === 'upload') {
        setAttachments(prev => [...prev, newAttachment]);
      }
      setReferencedImage(null);
    } catch (error) {
      console.error('转换引用图片失败:', error);
      alert('转换引用图片失败，请重试');
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (sending) return;
    if (mode === 'generate' && !text) return;
    if (mode === 'upload' && attachments.length === 0) return;

    if (!systemLogin.isLoggedIn) {
      alert('请先登录');
      return;
    }

    if (!systemLogin.currentUserChatSession) {
      try {
        await systemLogin.getOrCreateLatestUserChatSession();
      } catch (e) {
        alert('创建会话失败，请重试');
        return;
      }
    }

    // 创建用户消息，包含附件信息
    const userMsg: ChatMessageItem = { 
      id: `${Date.now()}-u`, 
      role: 'user', 
      attachments: [...attachments],
      content: text || (mode === 'upload' ? '（已选择图片）' : '')
    };
    
    // 根据模式更新对应的消息列表
    if (mode === 'generate') {
      setGenerateMessages(prev => [...prev, userMsg]);
    } else {
      setUploadMessages(prev => [...prev, userMsg]);
    }
    setInput('');
    setSending(true);
    setIsUploading(true);

    // 上传附件到服务器
    const uploadedFileIds: string[] = [];
    if (attachments.length > 0) {
      try {
        for (let i = 0; i < attachments.length; i++) {
          const attachment = attachments[i];
          
          // 更新附件上传状态
          setAttachments(prev => prev.map(att => 
            att.id === attachment.id ? { ...att, isUploading: true } : att
          ));

          try {
            const fileId = await uploadSingleFile(attachment.file);
            uploadedFileIds.push(fileId);
            
            // 更新附件状态为上传完成
            setAttachments(prev => prev.map(att => 
              att.id === attachment.id ? { ...att, fileId, isUploading: false } : att
            ));
            
            console.log(`文件 ${attachment.file.name} 上传成功，文件ID: ${fileId}`);
          } catch (error) {
            console.error(`文件 ${attachment.file.name} 上传失败:`, error);
            // 更新附件状态为上传失败
            setAttachments(prev => prev.map(att => 
              att.id === attachment.id ? { ...att, isUploading: false } : att
            ));
            throw new Error(`文件 ${attachment.file.name} 上传失败: ${error instanceof Error ? error.message : '未知错误'}`);
          }
        }
      } catch (error) {
        console.error('文件上传失败:', error);
        const assistantMsg: ChatMessageItem = { 
          id: `${Date.now()}-a`, 
          role: 'assistant', 
          content: `文件上传失败: ${error instanceof Error ? error.message : '未知错误'}` 
        };
        if (mode === 'generate') {
          setGenerateMessages(prev => [...prev, assistantMsg]);
        } else {
          setUploadMessages(prev => [...prev, assistantMsg]);
        }
        setSending(false);
        setIsUploading(false);
        return;
      }
    }

    try {
      if (mode === 'generate') {
        // 图片生成请求
        const assistantMsg: ChatMessageItem = { 
          id: `${Date.now()}-a`, 
          role: 'assistant', 
          content: '正在为您生成图片...',
          isGenerating: true
        };
        setGenerateMessages(prev => [...prev, assistantMsg]);

        // 设置生成超时检测（30秒）
        const timeoutId = setTimeout(() => {
          console.log('图片生成超时');
          setGenerateMessages(prev => prev.map(msg => 
            msg.isGenerating ? { 
              ...msg, 
              content: '图片生成超时，请重试',
              isGenerating: false
            } : msg
          ));
          setGeneratingTimeoutId(null);
        }, 30000);
        setGeneratingTimeoutId(timeoutId);

        // 调用图片生成接口
        const generateResult = await systemLogin.generateImages(text);
        console.log('图片生成请求已发送:', generateResult);
        
        // 图片生成结果将通过 WebSocket 消息异步接收
        // 实际结果在 handleBusinessMessage 中处理
      } else if (mode === 'upload') {
        // 图片上传处理请求
        const assistantMsg: ChatMessageItem = { 
          id: `${Date.now()}-a`, 
          role: 'assistant', 
          content: '正在处理您上传的图片...',
          isGenerating: true
        };
        setUploadMessages(prev => [...prev, assistantMsg]);

        // 设置上传处理超时检测（60秒）
        const timeoutId = setTimeout(() => {
          console.log('图片上传处理超时');
          setUploadMessages(prev => prev.map(msg => 
            msg.isGenerating ? { 
              ...msg, 
              content: '图片处理超时，请重试',
              isGenerating: false
            } : msg
          ));
          setGeneratingTimeoutId(null);
        }, 10000);
        setGeneratingTimeoutId(timeoutId);

        // 这里应该调用图片上传处理接口，通过WebSocket接收结果
        const uploadResult = await systemLogin.uploadImages(text, uploadedFileIds[0]);
        console.log('图片上传处理请求已发送，文件IDs:', uploadResult);
        
        // 图片上传处理结果将通过 WebSocket 消息异步接收
        // 实际结果在 handleBusinessMessage 中处理
      }
    } catch (error) {
      // 清除超时检测
      if (generatingTimeoutId) {
        clearTimeout(generatingTimeoutId);
        setGeneratingTimeoutId(null);
      }
      
      const assistantMsg: ChatMessageItem = { id: `${Date.now()}-a`, role: 'assistant', content: '抱歉，服务暂时不可用，请稍后再试。' };
      if (mode === 'generate') {
        setGenerateMessages(prev => [...prev, assistantMsg]);
      } else {
        setUploadMessages(prev => [...prev, assistantMsg]);
      }
      // 可选：console 错误
      // eslint-disable-next-line no-console
      console.error('请求发送失败:', error);
    } finally {
      setSending(false);
      setIsUploading(false);
      // 清空附件列表和引用图片
      setAttachments([]);
      setReferencedImage(null);
      // 恢复输入框高度
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // 注意：不在这里清除超时，让WebSocket消息或超时自动清除
    }
  };

  const submitDesign = async () => {
    if (!systemLogin.isLoggedIn) {
      alert('请先登录');
      return;
    }
    if (!designDesc) {
      alert('请先填写描述');
      return;
    }
    if (!selectedImageUrl) {
      alert('请先选择图片');
      return;
    }
    setDesignSubmitting(true);
    try {
      const requestData: SubmitToyDesignedImageRequest = {
        submitId: editingItemId || undefined,
        submitDescription: designDesc,
        imageUrl: selectedImageUrl
      };

      const { response, isLoginNeeded } = await systemLogin.protectPostRequest<any>(
        '/app-api/ai/agent/submitToyDesignedImage',
        undefined,
        undefined,
        requestData
      );

      if (isLoginNeeded) {
        // 可能需要导航到登录页面
        return;
      }

      if (response?.code === 1 && response.data) {
        console.log(`提交设计成功:`, response.data);
        
        const data = response.data;
        if (data['result']) {
          alert(editingItemId ? '修改设计成功' : '提交设计成功');
          setDesignDesc('');
          setSelectedImageUrl(null);
          setEditingItemId(null);
          void loadDesignHistory();
        } else {
          alert('提交失败，请重试');
        }
      }
      else {
        alert('提交失败，请重试');
      }
    } catch (e) {
      alert('提交异常：' + (e instanceof Error ? e.message : '未知错误'));
    } finally {
      setDesignSubmitting(false);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setInput(e.target.value);
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  };

  const canSend = (() => {
    if (!isLoggedIn || sending || isUploading || !wsConnected) return false;
    const text = input.trim();
    if (mode === 'generate') return text.length > 0;
    if (mode === 'upload') return attachments.length > 0;
    return false;
  })();

  return (
    <div className={styles.container}>
      <div className={`${styles.mainLayout} ${isSidebarCollapsed ? styles.mainLayoutCollapsed : ''}`}>
        <aside className={`${styles.sidebar} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
          <div className={styles.sidebarInner}>
            <button className={styles.sidebarToggle} onClick={() => setIsSidebarCollapsed(v => !v)} title={isSidebarCollapsed ? '展开' : '收起'}>
              {isSidebarCollapsed ? '›' : '‹'}
            </button>
            <div className={styles.sidebarTitleIcon} title="操作">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9A1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.42 1.24 1.02 1.51H21a2 2 0 1 1 0 4h-.09c-.6.27-1.02.85-1.02 1.49z" strokeWidth="2"/>
              </svg>
            </div>
            <button className={`${styles.sidebarBtn} ${mode==='generate'?styles.active:''}`} onClick={handleMenuGenerateClick}>
              <span className={styles.sidebarIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeWidth="2"/>
                </svg>
              </span>
              生成玩具图片
            </button>
            <button className={`${styles.sidebarBtn} ${mode==='upload'?styles.active:''}`} onClick={handleMenuUploadClick}>
              <span className={styles.sidebarIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2"/>
                  <polyline points="7,10 12,5 17,10" strokeWidth="2"/>
                  <line x1="12" y1="5" x2="12" y2="20" strokeWidth="2"/>
                </svg>
              </span>
              上传玩具图片
            </button>
            <button className={`${styles.sidebarBtnPrimary} ${mode==='submit'?styles.active:''}`} onClick={handleMenuSubmitDesignClick}>
              <span className={styles.sidebarIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M5 12l5 5L20 7" strokeWidth="2"/>
                </svg>
              </span>
              提交设计
            </button>
            <div className={styles.sidebarFooter}>
              <div className={styles.sidebarTitle}>提交记录</div>
              <div className={styles.historyListFill}>
                {designHistory.length === 0 ? (
                  <div className={styles.historyEmpty}>暂无记录</div>
                ) : designHistory.map(item => (
                  <div 
                    key={item.submitId || Date.now() + Math.random()} 
                    className={styles.historyItem} 
                    title={item.submitDescription}
                    onClick={() => handleHistoryItemClick(item)}
                  >
                    <div className={styles.historyTitle}>{item.submitDescription || '未填写描述'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
        <div className={styles.content}>
        {/* 顶部一行：标题 + 状态 + 按钮 */}
        <div className={styles.topBar}>
          <div className={styles.modeTitle}>
            {mode === 'generate' ? '生成玩具图片' : mode === 'upload' ? '上传玩具图片' : '提交设计'}
          </div>
          <div className={styles.topStatus}>
            {!isLoggedIn ? (
              <div className={`${styles.statusIndicator} ${styles.statusError}`} title="需要登录">
                <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M18 6L6 18M6 6l12 12" strokeWidth="2"/>
                </svg>
                <span className={styles.statusText}>未登录</span>
              </div>
            ) : !systemLogin.currentUserChatSession ? (
              <div className={`${styles.statusIndicator} ${styles.statusWarning}`} title="正在初始化会话">
                <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" strokeWidth="2"/>
                </svg>
                <span className={styles.statusText}>准备会话…</span>
              </div>
            ) : !wsConnected ? (
              <div className={`${styles.statusIndicator} ${styles.statusWarning}`} title={`正在连接服务器${wsReconnectAttempts > 0 ? ` (重试 ${wsReconnectAttempts}/5)` : ''}`}>
                <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" strokeWidth="2"/>
                  <polyline points="3.27,6.96 12,12.01 20.73,6.96" strokeWidth="2"/>
                  <line x1="12" y1="22.08" x2="12" y2="12" strokeWidth="2"/>
                </svg>
                <span className={styles.statusText}>连接中…</span>
              </div>
            ) : (
              <div className={`${styles.statusIndicator} ${styles.statusSuccess}`} title={`连接已建立${heartbeatMissed > 0 ? ` (心跳丢失: ${heartbeatMissed}/${MAX_HEARTBEAT_MISSED})` : ''}`}>
                <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="2"/>
                  <polyline points="22,4 12,14.01 9,11.01" strokeWidth="2"/>
                </svg>
                <span className={styles.statusText}>已连接</span>
              </div>
            )}
          </div>
          <div className={styles.topActions}>
            <button className={styles.actionBtn} onClick={() => navigate('/recharge')}>充值</button>
            {systemLogin.isLoggedIn ? (
              <div className={styles.userDropdown} ref={userDropdownRef}>
                <button 
                  className={styles.userDropdownToggle}
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  title="用户选项"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeWidth="2"/>
                    <circle cx="12" cy="7" r="4" strokeWidth="2"/>
                  </svg>
                </button>
                {isUserDropdownOpen && (
                  <div className={styles.userDropdownMenu}>
                    <button 
                      className={styles.userDropdownItem}
                      onClick={() => {
                        setIsUserDropdownOpen(false);
                        navigate('/profile');
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeWidth="2"/>
                        <circle cx="12" cy="7" r="4" strokeWidth="2"/>
                      </svg>
                      用户信息
                    </button>
                    <button 
                      className={styles.userDropdownItem}
                      onClick={() => {
                        setIsUserDropdownOpen(false);
                        const ok = apiClient.logout();
                        if (ok) navigate('/');
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeWidth="2"/>
                        <polyline points="16,17 21,12 16,7" strokeWidth="2"/>
                        <line x1="21" y1="12" x2="9" y2="12" strokeWidth="2"/>
                      </svg>
                      注销
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className={styles.actionBtn} onClick={() => navigate('/login')}>登录</button>
            )}
          </div>
        </div>

        {/* 右侧主体：submit 模式显示提交面板，否则显示聊天 */}
        {mode !== 'submit' ? (
        <>
        <div ref={listRef} className={styles.messageList} style={{ overflowY: 'auto', flex: 1 }}>
          {currentMessages.length === 0 && (
            <div className={styles.placeholder}>开始与智能助理对话吧</div>
          )}

          {currentMessages.map(m => (
            <div key={m.id} className={`${styles.messageRow} ${m.role === 'assistant' ? styles.assistantRow : styles.userRow}`}>
              {m.role === 'assistant' && (
                <div className={`${styles.avatar} ${styles.avatarAssistant}`}>AI</div>
              )}
              <div className={`${styles.bubble} ${m.role === 'assistant' ? styles.bubbleAssistant : styles.bubbleUser}`}>
                {m.imageUrl && (
                  <div 
                    className={styles.imageContainer}
                    onMouseEnter={() => setHoveredImageId(m.id)}
                    onMouseLeave={() => setHoveredImageId(null)}
                  >
                    <img src={m.imageUrl} alt="生成的图片" className={styles.generatedImage} />
                    {hoveredImageId === m.id && (
                      <div className={styles.imageActions}>
                        <button 
                          className={styles.imageActionBtn}
                          onClick={() => handleImageReference(m.imageUrl!, m.id, m.content)}
                          title="引用此图片"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeWidth="2"/>
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeWidth="2"/>
                          </svg>
                        </button>
                        <button 
                          className={styles.imageActionBtn}
                          onClick={() => downloadImage(m.imageUrl!, m.id)}
                          title="下载图片"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2"/>
                            <polyline points="7,10 12,15 17,10" strokeWidth="2"/>
                            <line x1="12" y1="15" x2="12" y2="3" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {m.content && (
                  <div className={styles.messageContent}>
                    {m.content}
                  </div>
                )}
                {m.attachments && m.attachments.length > 0 && (
                  <div className={styles.attachmentsContainer}>
                    {m.attachments.map(attachment => (
                      <div key={attachment.id} className={styles.attachmentItem}>
                        {attachment.file.type.startsWith('image/') ? (
                          <img src={attachment.url} alt={attachment.file.name} className={styles.attachmentImage} />
                        ) : (
                          <div className={styles.attachmentFile}>
                            <div className={styles.fileIcon}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2"/>
                                <polyline points="14,2 14,8 20,8" strokeWidth="2"/>
                              </svg>
                            </div>
                            <div className={styles.fileInfo}>
                              <div className={styles.fileName}>{attachment.file.name}</div>
                              <div className={styles.fileSize}>
                                {(attachment.file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          </div>
                        )}
                        {attachment.isUploading && (
                          <div className={styles.uploadingOverlay}>
                            <span className={styles.loadingDots}>
                              <span className={styles.dot}></span>
                              <span className={styles.dot}></span>
                              <span className={styles.dot}></span>
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {m.isGenerating && (
                  <span className={styles.loadingDots}>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                  </span>
                )}
              </div>
              {m.role === 'user' && (
                <div className={`${styles.avatar} ${styles.avatarUser}`}>我</div>
              )}
            </div>
          ))}

          {sending && (
            <div className={`${styles.messageRow} ${styles.assistantRow}`}>
              <div className={`${styles.avatar} ${styles.avatarAssistant}`}>AI</div>
              <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
                正在思考
                <span className={styles.loadingDots}>
                  <span className={styles.dot}></span>
                  <span className={styles.dot}></span>
                  <span className={styles.dot}></span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.inputWrap}>
          {/* 引用图片显示区域 */}
          {referencedImage && mode === 'upload' && (
            <div className={styles.referencedImagePreview}>
              <div className={styles.referencedImageCard}>
                <img src={referencedImage.url} alt="引用的图片" className={styles.referencedImage} />
                <div className={styles.referencedImageInfo}>
                  <div className={styles.referencedImageLabel}>引用图片</div>
                  <div className={styles.referencedImageContent}>{referencedImage.messageContent}</div>
                </div>
                <div className={styles.referencedImageActions}>
                  <button 
                    className={styles.convertToFileBtn}
                    onClick={convertReferencedImageToFile}
                    title="作为文件上传"
                  >
                    作为文件上传
                  </button>
                  <button 
                    className={styles.removeReferencedImageBtn}
                    onClick={removeReferencedImage}
                    title="取消引用"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 附件预览区域 */}
          {attachments.length > 0 && mode === 'upload' && (
            <div className={styles.attachmentsPreview}>
              {attachments.map(attachment => (
                <div key={attachment.id} className={styles.attachmentPreview}>
                  {attachment.file.type.startsWith('image/') ? (
                    <img src={attachment.url} alt={attachment.file.name} className={styles.previewImage} />
                  ) : (
                    <div className={styles.previewFile}>
                      <div className={styles.fileIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2"/>
                          <polyline points="14,2 14,8 20,8" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className={styles.fileName}>{attachment.file.name}</div>
                    </div>
                  )}
                  <button 
                    className={styles.removeAttachmentBtn}
                    onClick={() => removeAttachment(attachment.id)}
                    disabled={attachment.isUploading}
                  >
                    ×
                  </button>
                  {attachment.isUploading && (
                    <div className={styles.uploadingIndicator}>
                      <span className={styles.loadingDots}>
                        <span className={styles.dot}></span>
                        <span className={styles.dot}></span>
                        <span className={styles.dot}></span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={styles.inputCard}>
            <div className={styles.inputRow}>
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                placeholder={systemLogin.isLoggedIn ? '输入消息，按 Enter 发送，Shift+Enter 换行' : '请先登录后再聊天'}
                disabled={!systemLogin.isLoggedIn || sending}
                value={input}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button 
                className={styles.attachBtn}
                onClick={triggerFileInput}
                disabled={!systemLogin.isLoggedIn || sending || isUploading || mode !== 'upload'}
                title="上传文件"
                aria-label="上传文件"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49" strokeWidth="2"/>
                </svg>
              </button>
                <button 
                  className={styles.sendBtn} 
                  onClick={handleSend} 
                  disabled={!canSend}
                  aria-label="发送"
                  title="发送"
                >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M5 19l14-7L5 5v5l10 2-10 2v5z" strokeWidth="2"/>
                </svg>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={"image/*"}
              onChange={handleFileUpload}
              className={styles.hiddenInput}
            />
            <div className={styles.toolbar}>
              <div className={styles.hint}>
                {!isLoggedIn ? '请先登录' : !wsConnected ? '连接中…' : (mode === 'generate' ? '生成图片：仅文本' : '上传图片：需选择图片后发送')}
              </div>
            </div>
          </div>
        </div>
        </>
        ) : (
          <div className={styles.submitPane}>
            <div className={styles.submitCard}>
              <div className={styles.submitTitle}>
                {editingItemId ? '编辑设计' : '提交设计'}
              </div>
              <textarea
                className={styles.submitTextarea}
                placeholder={'请输入提交描述'}
                value={designDesc}
                onChange={(e) => setDesignDesc(e.target.value)}
                rows={4}
              />
              
              <div className={styles.imageSelectionSection}>
                <div className={styles.sectionTitle}>选择图片</div>
                {loadingImages ? (
                  <div className={styles.loadingImages}>
                    <span className={styles.loadingDots}>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                    </span>
                    正在加载图片列表...
                  </div>
                ) : availableImages.length === 0 ? (
                  <div className={styles.noImagesHint}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2"/>
                      <polyline points="21,15 16,10 5,21" strokeWidth="2"/>
                    </svg>
                    <div className={styles.noImagesText}>
                      <div>暂无图片</div>
                      <div className={styles.noImagesSubText}>请先生成或上传图片</div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.imageGrid}>
                    {availableImages.map((imageUrl, index) => (
                      <div 
                        key={`image-${index}`} 
                        className={`${styles.imageOption} ${selectedImageUrl === imageUrl ? styles.imageOptionSelected : ''}`}
                        onClick={() => handleImageSelect(imageUrl)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`生成的图片 ${index + 1}`} 
                          className={styles.imageOptionImg}
                        />
                        {selectedImageUrl === imageUrl && (
                          <div className={styles.selectedIndicator}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="2"/>
                              <polyline points="22,4 12,14.01 9,11.01" strokeWidth="2"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className={styles.submitActions}>
                <button className={styles.submitBtnPrimary} disabled={designSubmitting || !selectedImageUrl} onClick={submitDesign}>
                  {designSubmitting ? (editingItemId ? '修改中…' : '提交中…') : (editingItemId ? '修改' : '提交')}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default IndexPage;


